import { type FC, useMemo, useRef } from 'react';
import type { Key } from 'react';
import Barchart from 'components/molecules/Barchart';
import MultiLineChart from 'components/molecules/MultiLineChart';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { groupBarChartWidth } from 'components/organism/PVMAnalysis/PVMInfiniteImpactChartContainer';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { formatNumWithKMBAlt, scaleMaxValue } from 'utils/helperMethods';
import { TPO_METRIC_TREND_CHART_PANEL } from '../../../config/testIds';
import { useCustomerConfig } from '../../../context/CustomerConfigContext';
import type { Granularity, PeriodColumn, PlanMetricChartBounds, PlanMetricDimensionRow } from '../types';

const CHART_CONTAINER_ID = 'plan-metric-trend-chart-container';
const Y_AXIS_CHART_ID = 'plan-metric-trend-chart-y-axis';
const MAIN_CHART_ID = 'plan-metric-trend-chart-main';

const LINE_PALETTE: string[] = [
    '#0EA5A4',
    '#F97316',
    '#8B5CF6',
    '#2563EB',
    '#DB2777',
    '#16A34A',
    '#CA8A04',
    '#0891B2',
    '#9333EA',
    '#DC2626',
];

const colorFor = (idx: number): string => LINE_PALETTE[idx % LINE_PALETTE.length];

const GRANULARITY_AXIS_LABEL: Record<Granularity, string> = {
    W: 'Weeks',
    M: 'Months',
    Q: 'Quarters',
};

interface TrendChartPanelProps {
    dataSource: PlanMetricDimensionRow[];
    periods: PeriodColumn[];
    kpiKey: string;
    granularity: Granularity;
    selectedRowKeys: Key[];
    chartBounds: PlanMetricChartBounds;
    height: number;
}

const TrendChartPanel: FC<TrendChartPanelProps> = ({
    dataSource,
    periods,
    kpiKey,
    granularity,
    selectedRowKeys,
    chartBounds,
    height,
}) => {
    const currentRef = useRef<HTMLDivElement | null>(null);
    const containerWidth = useContainerWidth({ containerId: CHART_CONTAINER_ID });

    const { getKpiName } = useCustomerConfig();
    const kpiName = getKpiName(kpiKey);
    const xAxisTitle = GRANULARITY_AXIS_LABEL[granularity];

    // Lookup rows by attributeExId so the chart preserves checkbox click order
    // when iterating `selectedRowKeys` (Antd does not guarantee a particular
    // order on dataSource itself).
    const rowsByKey = useMemo(() => {
        const map = new Map<string, PlanMetricDimensionRow>();
        for (const row of dataSource) {
            if (row.attributeExId) map.set(row.attributeExId, row);
        }
        return map;
    }, [dataSource]);

    const selectedRows = useMemo<PlanMetricDimensionRow[]>(() => {
        const out: PlanMetricDimensionRow[] = [];
        for (const key of selectedRowKeys) {
            const row = rowsByKey.get(String(key));
            if (row) out.push(row);
        }
        return out;
    }, [selectedRowKeys, rowsByKey]);

    const hasEnoughSelections = selectedRows.length >= 1;

    const colorMapping = useMemo<Record<string, string>>(() => {
        const map: Record<string, string> = {};
        selectedRows.forEach((row, idx) => {
            map[row.attributeDesc] = colorFor(idx);
        });
        return map;
    }, [selectedRows]);

    const chartDataset = useMemo(() => {
        if (!hasEnoughSelections) return [];
        const dataset: Array<{
            period: string;
            kpiValue: number;
            kpiName: string;
            type: string;
        }> = [];
        for (const row of selectedRows) {
            for (const p of periods) {
                const v = row[p.key];
                if (typeof v === 'number') {
                    dataset.push({
                        period: p.label,
                        kpiValue: v,
                        kpiName: row.attributeDesc,
                        type: row.attributeDesc,
                    });
                }
            }
        }
        return dataset;
    }, [hasEnoughSelections, selectedRows, periods]);

    const minBound = useMemo(() => Math.min(0, chartBounds.min), [chartBounds.min]);
    const maxBound = useMemo(() => scaleMaxValue(20, chartBounds.max), [chartBounds.max]);

    const graphWidth = Math.max(periods.length * groupBarChartWidth, containerWidth || 0);

    const excelExportData = useMemo(() => {
        const names = selectedRows.map((r) => r.attributeDesc);
        const headers = ['Period', ...names];
        const data = periods.map((p) => [
            p.label,
            ...selectedRows.map((row) => {
                const v = row[p.key];
                return typeof v === 'number' ? v : '';
            }),
        ]);
        return { headers, data };
    }, [selectedRows, periods]);

    return (
        <div
            className="mt-2 mb-2 flex flex-col border border-[#E4E4E7] rounded-lg px-4 py-3 w-full bg-white"
            data-testid={TPO_METRIC_TREND_CHART_PANEL}
        >
            {/* // <div className="mt-3  mb-24 flex flex-col border border-[#E4E4E7] rounded-lg px-4 py-3 w-full bg-white"> */}
            <div className="flex flex-row items-center justify-between">
                <p className="text-sm font-semibold text-[#18181B]">{'Trend Chart'}</p>
                {hasEnoughSelections && (
                    <div className="flex flex-row gap-x-2 items-center">
                        <CopyToClipBoard refOfComponent={currentRef} chartOnly />
                        <DownloadExcel
                            title={'Trend Chart'}
                            excelData={excelExportData.data}
                            headers={excelExportData.headers}
                        />
                    </div>
                )}
            </div>

            {hasEnoughSelections ? (
                <div ref={currentRef}>
                    <div className="items-center flex flex-row justify-between my-2 mb-3 gap-x-4">
                        <div className="text-sm">
                            <p>{kpiName}</p>
                        </div>
                        <div className="flex flex-row items-center justify-center gap-x-4 flex-wrap">
                            {selectedRows.map((row, idx) => (
                                <div
                                    key={row.attributeExId}
                                    className="flex flex-row items-center gap-x-1 text-xs text-[#52525C]"
                                >
                                    <div style={{ backgroundColor: colorFor(idx) }} className="w-3 h-2" />
                                    <p>{row.attributeDesc}</p>
                                </div>
                            ))}
                        </div>
                        <div />
                    </div>

                    <div className="flex">
                        <Barchart
                            padding={[0, 100, 15, 0]}
                            barWidth={0}
                            xAxisConfig={false}
                            dataset={[]}
                            height={height}
                            loading={false}
                            chartContainer={Y_AXIS_CHART_ID}
                            fields={['period', 'kpiValue', 'type']}
                            minBound={minBound}
                            maxBound={maxBound}
                            width={70}
                            colorKey={'type'}
                            YGrid={false}
                            yAxisConfig={true}
                            yAxisLabelFormatter={(val: number) => formatNumWithKMBAlt(val, 2, 2)}
                        />
                        <HorizontalInfiniteScroller
                            id={CHART_CONTAINER_ID}
                            hasMore={false}
                            scrollThreshold={0.9}
                            onNext={() => {
                                /* trend chart is bounded by visible periods - no pagination */
                            }}
                        >
                            <div id={CHART_CONTAINER_ID} className="overflow-x-auto max-w-full pb-[60px]">
                                <MultiLineChart
                                    xAxisConfig
                                    lineStyle={{ opacity: 1, lineWidth: 1.2 }}
                                    showLegend={false}
                                    padding={[5, 0, 5, 0]}
                                    dataset={chartDataset}
                                    height={height}
                                    loading={false}
                                    chartContainer={MAIN_CHART_ID}
                                    fields={['period', 'kpiValue']}
                                    minBound={minBound}
                                    maxBound={maxBound}
                                    width={graphWidth}
                                    xAxisLabelFormatter={(val: string) => val}
                                    colorKey={'kpiName'}
                                    colorMapping={colorMapping}
                                    showLoader={false}
                                    tooltipFormatter={(xAxis, val) => ({
                                        name: xAxis,
                                        value: `${kpiName}: ${formatNumWithKMBAlt(val, 2, 2)}`,
                                    })}
                                />
                            </div>
                        </HorizontalInfiniteScroller>
                    </div>

                    <div className="text-sm text-center text-[#52525C] mt-1">
                        <p>{xAxisTitle}</p>
                    </div>
                </div>
            ) : (
                <div
                    className="flex items-center justify-center text-sm text-[#52525C]"
                    style={{ height }}
                    data-testid={`${TPO_METRIC_TREND_CHART_PANEL}_empty`}
                >
                    {'Select at least 1 row to view the trend.'}
                </div>
            )}
        </div>
    );
};

TrendChartPanel.displayName = 'TrendChartPanel';
export default TrendChartPanel;
